/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { Table, Tag, Button, Input, Select, Space, Card, Typography, message, Tooltip } from 'antd';
import {
  SearchOutlined,
  CheckCircleOutlined,
  StopOutlined,
  FilePdfOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { getAuthHeaders } from './jobManagementUtils';
import './JobManagement.css';

const { Text } = Typography;
const { Option } = Select;

function JobSeekerManagement(props) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const authHeaders = getAuthHeaders(props.permitdata?.token);

  const fetchSeekers = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        (props.app_hostname || '') + '/api/account/management/jobs/seekers',
        {
          keyword: searchText,
          status: statusFilter,
        },
        { headers: authHeaders }
      );
      if (res.data && res.data.status === 'Success') {
        setData(res.data.data?.items || []);
      }
    } catch (err) {
      message.error('ไม่สามารถดึงข้อมูลผู้สมัครงานได้');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSeekers();
  }, [statusFilter]);

  const handleStatusChange = async (userId, newStatus) => {
    try {
      await axios.post(
        (props.app_hostname || '') + '/api/account/management/jobs/user_status',
        {
          user_id: userId,
          status: newStatus,
        },
        { headers: authHeaders }
      );
      setData((prev) =>
        prev.map((item) => (item.id === userId ? { ...item, status: newStatus } : item))
      );
      message.success('ปรับปรุงสถานะผู้ใช้งานเรียบร้อยแล้ว');
    } catch (err) {
      message.error('เกิดข้อผิดพลาดในการปรับสถานะ');
    }
  };

  const filteredData = data.filter((item) => {
    const matchSearch =
      !searchText ||
      item.full_name?.toLowerCase().includes(searchText.toLowerCase()) ||
      item.phone?.includes(searchText) ||
      item.category_name?.toLowerCase().includes(searchText.toLowerCase());
    return matchSearch;
  });

  const columns = [
    {
      title: 'ผู้สมัครงาน (Job Seeker)',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 600, color: '#1890ff', fontSize: 14 }}>{name || record.username || 'ไม่ระบุชื่อ'}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>
            เบอร์โทร: {record.phone} {record.email ? `| ${record.email}` : ''}
          </div>
          {record.headline && (
            <div style={{ fontSize: 12, color: '#595959', fontStyle: 'italic', marginTop: 2 }}>
              "{record.headline}"
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'สายวิชาชีพ',
      dataIndex: 'category_name',
      key: 'category_name',
      render: (cat) => <Tag color='geekblue'>{cat || 'ไม่ระบุ'}</Tag>,
    },
    {
      title: 'จังหวัด / ที่อยู่',
      dataIndex: 'province',
      key: 'province',
      render: (prov) => <Tag color='cyan'>{prov || 'ไม่ระบุ'}</Tag>,
    },
    {
      title: 'เงินเดือนที่คาดหวัง',
      dataIndex: 'expected_salary',
      key: 'expected_salary',
      render: (salary) =>
        salary > 0 ? (
          <span style={{ fontWeight: 500, color: '#389e0d' }}>{salary.toLocaleString()} บาท</span>
        ) : (
          'ตามตกลง'
        ),
    },
    {
      title: 'ไฟล์ Resume',
      dataIndex: 'resume_file_url',
      key: 'resume_file_url',
      render: (url) =>
        url ? (
          <Button
            size='small'
            type='link'
            icon={<FilePdfOutlined />}
            href={url}
            target='_blank'
            style={{ padding: 0 }}
          >
            เปิดดู Resume
          </Button>
        ) : (
          <Text type='secondary' style={{ fontSize: 12 }}>ยังไม่อัปโหลด</Text>
        ),
    },
    {
      title: 'สถานะยืนยันเบอร์',
      dataIndex: 'is_phone_verified',
      key: 'is_phone_verified',
      render: (verified) =>
        verified ? (
          <Tag color='success'>ยืนยัน OTP แล้ว</Tag>
        ) : (
          <Tag color='warning'>ยังไม่ยืนยัน</Tag>
        ),
    },
    {
      title: 'สถานะบัญชี',
      dataIndex: 'status',
      key: 'status',
      render: (status) =>
        status === 'active' ? (
          <Tag color='success'>ปกติ</Tag>
        ) : (
          <Tag color='error'>ระงับการใช้งาน</Tag>
        ),
    },
    {
      title: 'จัดการ',
      key: 'actions',
      render: (_, record) => (
        <Space size='small'>
          {record.status === 'suspended' ? (
            <Button
              size='small'
              icon={<CheckCircleOutlined />}
              onClick={() => handleStatusChange(record.id, 'active')}
              type='text'
              style={{ color: '#52c41a' }}
            >
              ปลดระงับ
            </Button>
          ) : (
            <Button
              size='small'
              danger
              icon={<StopOutlined />}
              onClick={() => handleStatusChange(record.id, 'suspended')}
              type='text'
            >
              ระงับ
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className='job-management-container'>
      <div className='job-management-header'>
        <div>
          <h2>👨‍⚕️ จัดการบัญชีผู้หางาน (Job Seeker Directory)</h2>
          <Text type='secondary'>
            ดึงข้อมูลผู้สมัครงานจริงจากฐานข้อมูล ({data.length} บัญชี) ตรวจสอบประวัติ การยืนยันเบอร์โทรศัพท์ และเอกสาร Resume
          </Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={fetchSeekers}>
          รีเฟรช
        </Button>
      </div>

      <Card className='job-management-card'>
        <div className='job-filter-bar'>
          <Input
            placeholder='ค้นหาชื่อผู้สมัคร, เบอร์โทร, สายวิชาชีพ...'
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 280 }}
            allowClear
          />
          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 160 }}>
            <Option value='all'>สถานะทั้งหมด</Option>
            <Option value='active'>ใช้งานปกติ (Active)</Option>
            <Option value='suspended'>ระงับ (Suspended)</Option>
          </Select>
        </div>

        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey='id'
          loading={loading}
          pagination={{ pageSize: 10, showTotal: (total) => `รวม ${total} บัญชี` }}
        />
      </Card>
    </div>
  );
}

export default JobSeekerManagement;
